// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useRef, useState } from 'react'
import * as api from '../../api/client'

/**
 * SaaS-admin: build each pharmacy's email templates (subject + body per type). Variables are a fixed
 * PHI-safe whitelist. Save overrides the platform default; Reset removes the override.
 */
export default function PharmacyEmailTemplatesCard({ pharmacyId }: { pharmacyId: string }) {
  const [tpls, setTpls] = useState<api.EmailTemplate[]>([])
  const [key, setKey] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [preview, setPreview] = useState<{ subject: string; body_text: string } | null>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const current = tpls.find(t => t.key === key)

  function selectTemplate(t: api.EmailTemplate) {
    setKey(t.key); setSubject(t.subject); setBody(t.body_text); setMsg(null); setPreview(null)
  }
  async function load(selectKey?: string) {
    const r = await api.listPharmacyEmailTemplates(pharmacyId)
    setTpls(r.items)
    const pick = r.items.find(t => t.key === (selectKey || key)) || r.items[0]
    if (pick) selectTemplate(pick)
  }
  useEffect(() => { load().catch(() => {}) }, [pharmacyId])

  function insertVar(name: string) {
    const token = `{{${name}}}`
    const el = bodyRef.current
    if (!el) { setBody(b => b + token); return }
    const start = el.selectionStart ?? body.length
    setBody(body.slice(0, start) + token + body.slice(el.selectionStart ?? body.length))
    setTimeout(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length) }, 0)
  }

  async function save() {
    if (!current) return
    setBusy(true); setMsg(null)
    try { await api.putPharmacyEmailTemplate(pharmacyId, current.key, subject, body); await load(current.key); setMsg('Saved.') }
    catch (e: any) { setMsg(e?.message || 'Save failed.') }
    finally { setBusy(false) }
  }
  async function reset() {
    if (!current) return
    setBusy(true); setMsg(null)
    try { await api.resetPharmacyEmailTemplate(pharmacyId, current.key); await load(current.key); setMsg('Reset to default.') }
    catch (e: any) { setMsg(e?.message || 'Reset failed.') }
    finally { setBusy(false) }
  }
  async function doPreview() {
    if (!current) return
    setBusy(true); setMsg(null)
    try { setPreview(await api.previewPharmacyEmailTemplate(pharmacyId, current.key, subject, body)) }
    catch (e: any) { setMsg(e?.message || 'Preview failed.') }
    finally { setBusy(false) }
  }

  const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginTop: 16, background: 'var(--color-surface)' }
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--color-text-mute)', display: 'block', marginBottom: 3 }
  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 13, fontFamily: 'inherit' }

  return (
    <div style={card}>
      <h3 style={{ fontSize: 15, margin: '0 0 4px' }}>Email templates</h3>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 12 }}>
        Control the wording of this pharmacy's emails. Use the variables below — patient data is never available, by design.
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {tpls.map(t => (
          <button key={t.key} onClick={() => selectTemplate(t)}
            style={{ border: '1px solid var(--color-border)', borderRadius: 16, padding: '4px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
              background: t.key === key ? 'var(--color-primary,#2563eb)' : 'none', color: t.key === key ? '#fff' : 'var(--color-text)' }}>
            {t.label}{t.is_custom ? ' •' : ''}
          </button>
        ))}
      </div>

      {current && (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>Subject</label>
            <input style={input} value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={label}>Body</label>
            <textarea ref={bodyRef} value={body} onChange={e => setBody(e.target.value)} rows={8}
              style={{ ...input, resize: 'vertical', lineHeight: 1.5 }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginBottom: 4 }}>Insert variable:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {current.variables.map(v => (
                <button key={v.name} onClick={() => insertVar(v.name)} title={v.description}
                  style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '3px 8px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'monospace', background: 'var(--color-bg-subtle,#f8fafc)' }}>
                  {`{{${v.name}}}`}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={save} disabled={busy} style={{ background: 'var(--color-primary,#2563eb)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>Save</button>
            <button onClick={doPreview} disabled={busy} style={{ background: 'none', border: '1px solid var(--color-primary,#2563eb)', color: 'var(--color-primary,#2563eb)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Preview</button>
            {current.is_custom && <button onClick={reset} disabled={busy} style={{ background: 'none', border: 'none', color: 'var(--color-text-mute)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>Reset to default</button>}
            {msg && <span style={{ fontSize: 12.5, color: msg.includes('fail') || msg.includes('Fail') ? '#B23A22' : 'var(--color-success,#15803d)' }}>{msg}</span>}
          </div>

          {preview && (
            <div style={{ marginTop: 14, border: '1px solid var(--color-border)', borderRadius: 8, padding: 12, background: 'var(--color-bg-subtle,#f8fafc)' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginBottom: 4 }}>PREVIEW (sample data)</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{preview.subject}</div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{preview.body_text}</div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
